// src/services/subgraph.js
// PGT Ecosystem — Shared Subgraph Client
// Queries The Graph's pgt-ecosystem subgraph directly from any frontend.
// No server dependency. Works in React Native, Vite, and CRA apps.

const DEFAULT_SUBGRAPH_URL = 'https://api.studio.thegraph.com/query/1740072/pgt-ecosystem-base/version/latest';

/** Resolve the subgraph URL from any env-var style (Vite, CRA, RN, Node) */
function getSubgraphUrl() {
  if (typeof process !== 'undefined' && process.env) {
    return (
      process.env.VITE_SUBGRAPH_URL ||
      process.env.REACT_APP_SUBGRAPH_URL ||
      process.env.SUBGRAPH_URL ||
      DEFAULT_SUBGRAPH_URL
    );
  }
  // Vite exposes import.meta.env
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.VITE_SUBGRAPH_URL || DEFAULT_SUBGRAPH_URL;
    }
  } catch (_) { /* ignore */ }
  return DEFAULT_SUBGRAPH_URL;
}

function getSubgraphApiKey() {
  if (typeof process !== 'undefined' && process.env) {
    return (
      process.env.VITE_SUBGRAPH_API_KEY ||
      process.env.REACT_APP_SUBGRAPH_API_KEY ||
      process.env.SUBGRAPH_API_KEY ||
      ''
    );
  }
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.VITE_SUBGRAPH_API_KEY || '';
    }
  } catch (_) { /* ignore */ }
  return '';
}

// ---------------------------------------------------------------------------
// Core fetch helper
// ---------------------------------------------------------------------------

async function querySubgraph(query, variables = {}) {
  const url = getSubgraphUrl();
  const apiKey = getSubgraphApiKey();
  const headers = { 'Content-Type': 'application/json' };
  if (apiKey) headers['Authorization'] = `Bearer ${apiKey}`;

  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    throw new Error(`Subgraph query failed: ${res.status} ${res.statusText}`);
  }

  const json = await res.json();
  if (json.errors) {
    throw new Error(`Subgraph error: ${json.errors.map(e => e.message).join(', ')}`);
  }
  return json.data;
}

// ---------------------------------------------------------------------------
// Wallet / Transfers
// ---------------------------------------------------------------------------

/** Get wallet info (balance, transfers count, ticket mints count) */
export async function getWallet(address) {
  const data = await querySubgraph(`
    query GetWallet($id: ID!) {
      wallet(id: $id) {
        id
        address
        pgtBalance
        totalTransfers
        totalTicketMints
      }
    }
  `, { id: address.toLowerCase() });
  return data.wallet || null;
}

/** Get PGT transfer history for a wallet (sent + received) */
export async function getTransferHistory(address, { first = 50, skip = 0, orderDirection = 'desc' } = {}) {
  const addr = address.toLowerCase();
  const data = await querySubgraph(`
    query GetTransfers($addr: Bytes!, $first: Int!, $skip: Int!, $dir: OrderDirection!) {
      sent: transfers(
        where: { from: $addr }
        orderBy: timestamp, orderDirection: $dir
        first: $first, skip: $skip
      ) { id from to value timestamp blockNumber txHash }
      received: transfers(
        where: { to: $addr }
        orderBy: timestamp, orderDirection: $dir
        first: $first, skip: $skip
      ) { id from to value timestamp blockNumber txHash }
    }
  `, { addr, first, skip, dir: orderDirection });

  // Merge and sort
  const all = [...(data.sent || []), ...(data.received || [])];
  all.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
  return all.slice(0, first);
}

/** Get all transfers (global, for admin dashboards) */
export async function getAllTransfers({ first = 100, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query AllTransfers($first: Int!, $skip: Int!) {
      transfers(orderBy: timestamp, orderDirection: desc, first: $first, skip: $skip) {
        id from to value timestamp blockNumber txHash
      }
    }
  `, { first, skip });
  return data.transfers || [];
}

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** Get all events (on-chain EventEntity from EventTicketWithEvents) */
export async function getEvents({ first = 100, skip = 0, activeOnly = false } = {}) {
  const where = activeOnly ? '{ active: true }' : '{}';
  const data = await querySubgraph(`
    query GetEvents($first: Int!, $skip: Int!) {
      eventEntities(
        where: ${where}
        orderBy: createdAt, orderDirection: desc
        first: $first, skip: $skip
      ) {
        id eventId name description location imageURI startTime endTime organizer active
        tags metadataURI totalMints totalRevenue createdAt txHash
        ticketTypes { id eventId name price maxSupply totalMinted saleStart saleEnd }
      }
    }
  `, { first, skip });
  return data.eventEntities || [];
}

/** Get single event by eventId */
export async function getEvent(eventId) {
  const data = await querySubgraph(`
    query GetEvent($id: ID!) {
      eventEntity(id: $id) {
        id eventId name description location imageURI startTime endTime organizer active
        tags metadataURI totalMints totalRevenue createdAt txHash
        ticketTypes { id eventId name price maxSupply totalMinted saleStart saleEnd }
      }
    }
  `, { id: String(eventId) });
  return data.eventEntity || null;
}

// ---------------------------------------------------------------------------
// Tickets
// ---------------------------------------------------------------------------

/** Get ticket mints for a wallet address */
export async function getTicketsByOwner(address, { first = 100, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query GetTickets($owner: Bytes!, $first: Int!, $skip: Int!) {
      ticketMints(
        where: { owner: $owner }
        orderBy: timestamp, orderDirection: desc
        first: $first, skip: $skip
      ) {
        id owner ticketTypeId serialNumber tokenId timestamp blockNumber txHash
        event { id eventId name location startTime endTime organizer }
      }
    }
  `, { owner: address.toLowerCase(), first, skip });
  return data.ticketMints || [];
}

/** Get all ticket mints (global) */
export async function getAllTicketMints({ first = 100, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query AllMints($first: Int!, $skip: Int!) {
      ticketMints(orderBy: timestamp, orderDirection: desc, first: $first, skip: $skip) {
        id owner ticketTypeId serialNumber tokenId timestamp blockNumber txHash
        event { id eventId name }
      }
    }
  `, { first, skip });
  return data.ticketMints || [];
}

/** Get ticket types for an event */
export async function getTicketTypes(eventId) {
  const data = await querySubgraph(`
    query GetTicketTypes($eventId: BigInt!) {
      ticketTypes(where: { eventId: $eventId }) {
        id eventId name price maxSupply totalMinted saleStart saleEnd
        event { id name }
      }
    }
  `, { eventId: String(eventId) });
  return data.ticketTypes || [];
}

/** Get ticket payments by buyer */
export async function getPaymentsByBuyer(address, { first = 50 } = {}) {
  const data = await querySubgraph(`
    query GetPayments($buyer: Bytes!, $first: Int!) {
      ticketPayments(
        where: { buyer: $buyer }
        orderBy: timestamp, orderDirection: desc
        first: $first
      ) { id buyer totalPricePGT timestamp txHash }
    }
  `, { buyer: address.toLowerCase(), first });
  return data.ticketPayments || [];
}

// ---------------------------------------------------------------------------
// Check-ins & Perks
// ---------------------------------------------------------------------------

/** Get check-ins for a token */
export async function getCheckIns(tokenId) {
  const data = await querySubgraph(`
    query GetCheckIns($tokenId: BigInt!) {
      checkIns(where: { tokenId: $tokenId }, orderBy: timestamp, orderDirection: desc) {
        id tokenId timestamp txHash
      }
    }
  `, { tokenId: String(tokenId) });
  return data.checkIns || [];
}

/** Get all check-ins (for staff/admin) */
export async function getAllCheckIns({ first = 100, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query AllCheckIns($first: Int!, $skip: Int!) {
      checkIns(orderBy: timestamp, orderDirection: desc, first: $first, skip: $skip) {
        id tokenId timestamp txHash
      }
    }
  `, { first, skip });
  return data.checkIns || [];
}

/** Get perk claims for a token */
export async function getPerkClaims(tokenId) {
  const data = await querySubgraph(`
    query GetPerks($tokenId: BigInt!) {
      perkClaims(where: { tokenId: $tokenId }) {
        id tokenId perkType timestamp txHash
      }
    }
  `, { tokenId: String(tokenId) });
  return data.perkClaims || [];
}

// ---------------------------------------------------------------------------
// Marketplace
// ---------------------------------------------------------------------------

/** Get active marketplace listings */
export async function getActiveListings({ first = 50, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query ActiveListings($first: Int!, $skip: Int!) {
      listings(
        where: { active: true }
        orderBy: timestamp, orderDirection: desc
        first: $first, skip: $skip
      ) {
        id listingId seller nftContract tokenId amount pricePerToken
        paymentToken isAuction endTime active timestamp txHash
      }
    }
  `, { first, skip });
  return data.listings || [];
}

/** Get all listings (including inactive) */
export async function getAllListings({ first = 100, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query AllListings($first: Int!, $skip: Int!) {
      listings(orderBy: timestamp, orderDirection: desc, first: $first, skip: $skip) {
        id listingId seller nftContract tokenId amount pricePerToken
        paymentToken isAuction endTime active timestamp txHash
      }
    }
  `, { first, skip });
  return data.listings || [];
}

/** Get listings by seller address */
export async function getListingsBySeller(address, { first = 50 } = {}) {
  const data = await querySubgraph(`
    query SellerListings($seller: Bytes!, $first: Int!) {
      listings(
        where: { seller: $seller }
        orderBy: timestamp, orderDirection: desc
        first: $first
      ) {
        id listingId seller nftContract tokenId amount pricePerToken
        paymentToken isAuction endTime active timestamp txHash
      }
    }
  `, { seller: address.toLowerCase(), first });
  return data.listings || [];
}

/** Get marketplace sales */
export async function getSales({ first = 50, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query GetSales($first: Int!, $skip: Int!) {
      sales(orderBy: timestamp, orderDirection: desc, first: $first, skip: $skip) {
        id listingId buyer amount totalPrice timestamp txHash
      }
    }
  `, { first, skip });
  return data.sales || [];
}

/** Get sales by buyer */
export async function getSalesByBuyer(address, { first = 50 } = {}) {
  const data = await querySubgraph(`
    query BuyerSales($buyer: Bytes!, $first: Int!) {
      sales(
        where: { buyer: $buyer }
        orderBy: timestamp, orderDirection: desc
        first: $first
      ) { id listingId buyer amount totalPrice timestamp txHash }
    }
  `, { buyer: address.toLowerCase(), first });
  return data.sales || [];
}

// ---------------------------------------------------------------------------
// Swap
// ---------------------------------------------------------------------------

/** Get swap history for a buyer */
export async function getSwapsByBuyer(address, { first = 50 } = {}) {
  const data = await querySubgraph(`
    query BuyerSwaps($buyer: Bytes!, $first: Int!) {
      swapEvents(
        where: { buyer: $buyer }
        orderBy: timestamp, orderDirection: desc
        first: $first
      ) { id buyer payToken amountIn pgtOut timestamp txHash }
    }
  `, { buyer: address.toLowerCase(), first });
  return data.swapEvents || [];
}

/** Get all swaps (global) */
export async function getAllSwaps({ first = 100, skip = 0 } = {}) {
  const data = await querySubgraph(`
    query AllSwaps($first: Int!, $skip: Int!) {
      swapEvents(orderBy: timestamp, orderDirection: desc, first: $first, skip: $skip) {
        id buyer payToken amountIn pgtOut timestamp txHash
      }
    }
  `, { first, skip });
  return data.swapEvents || [];
}

// ---------------------------------------------------------------------------
// Global Stats / Dashboard
// ---------------------------------------------------------------------------

/** Get global platform statistics */
export async function getGlobalStats() {
  const data = await querySubgraph(`
    query GlobalStats {
      globalStats(id: "global") {
        totalTransfers
        totalTicketMints
        totalListings
        totalSales
        totalSwaps
        totalCheckIns
        totalEvents
        totalRevenuePGT
        uniqueWallets
        totalReferrals
        totalCouponRedemptions
        totalRewardClaims
        totalXPAwarded
      }
    }
  `);
  return data.globalStats || null;
}

// ---------------------------------------------------------------------------
// Combined: Full wallet activity (all types in one call)
// ---------------------------------------------------------------------------

/** Get combined activity for a wallet — transfers, tickets, payments, swaps, sales */
export async function getWalletActivity(address, { first = 25 } = {}) {
  const addr = address.toLowerCase();
  const data = await querySubgraph(`
    query WalletActivity($addr: Bytes!, $first: Int!) {
      wallet(id: $addr) {
        id pgtBalance totalTransfers totalTicketMints
      }
      sent: transfers(where: { from: $addr }, orderBy: timestamp, orderDirection: desc, first: $first) {
        id from to value timestamp txHash
      }
      received: transfers(where: { to: $addr }, orderBy: timestamp, orderDirection: desc, first: $first) {
        id from to value timestamp txHash
      }
      tickets: ticketMints(where: { owner: $addr }, orderBy: timestamp, orderDirection: desc, first: $first) {
        id ticketTypeId serialNumber tokenId timestamp txHash
        event { id name }
      }
      payments: ticketPayments(where: { buyer: $addr }, orderBy: timestamp, orderDirection: desc, first: $first) {
        id totalPricePGT timestamp txHash
      }
      swaps: swapEvents(where: { buyer: $addr }, orderBy: timestamp, orderDirection: desc, first: $first) {
        id amountIn pgtOut timestamp txHash
      }
      purchases: sales(where: { buyer: $addr }, orderBy: timestamp, orderDirection: desc, first: $first) {
        id listingId amount totalPrice timestamp txHash
      }
    }
  `, { addr, first });

  // Normalize into unified timeline
  const timeline = [];

  (data.sent || []).forEach(t => timeline.push({
    type: 'transfer_out', timestamp: Number(t.timestamp), txHash: t.txHash,
    to: t.to, value: t.value,
  }));
  (data.received || []).forEach(t => timeline.push({
    type: 'transfer_in', timestamp: Number(t.timestamp), txHash: t.txHash,
    from: t.from, value: t.value,
  }));
  (data.tickets || []).forEach(t => timeline.push({
    type: 'ticket_mint', timestamp: Number(t.timestamp), txHash: t.txHash,
    tokenId: t.tokenId, ticketTypeId: t.ticketTypeId, eventName: t.event?.name,
  }));
  (data.payments || []).forEach(t => timeline.push({
    type: 'ticket_payment', timestamp: Number(t.timestamp), txHash: t.txHash,
    amount: t.totalPricePGT,
  }));
  (data.swaps || []).forEach(t => timeline.push({
    type: 'swap', timestamp: Number(t.timestamp), txHash: t.txHash,
    amountIn: t.amountIn, pgtOut: t.pgtOut,
  }));
  (data.purchases || []).forEach(t => timeline.push({
    type: 'marketplace_buy', timestamp: Number(t.timestamp), txHash: t.txHash,
    totalPrice: t.totalPrice,
  }));

  timeline.sort((a, b) => b.timestamp - a.timestamp);

  return {
    wallet: data.wallet || null,
    timeline,
    sent: data.sent || [],
    received: data.received || [],
    tickets: data.tickets || [],
    payments: data.payments || [],
    swaps: data.swaps || [],
    purchases: data.purchases || [],
  };
}

// ---------------------------------------------------------------------------
// Health check
// ---------------------------------------------------------------------------

/** Check if subgraph is reachable and responding */
export async function healthCheck() {
  try {
    const stats = await getGlobalStats();
    return { ok: true, stats, url: getSubgraphUrl() };
  } catch (err) {
    return { ok: false, error: err.message, url: getSubgraphUrl() };
  }
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
  // Config
  getSubgraphUrl,
  healthCheck,
  querySubgraph,
  // Wallet
  getWallet,
  getTransferHistory,
  getAllTransfers,
  getWalletActivity,
  // Events
  getEvents,
  getEvent,
  // Tickets
  getTicketsByOwner,
  getAllTicketMints,
  getTicketTypes,
  getPaymentsByBuyer,
  // Check-ins
  getCheckIns,
  getAllCheckIns,
  getPerkClaims,
  // Marketplace
  getActiveListings,
  getAllListings,
  getListingsBySeller,
  getSales,
  getSalesByBuyer,
  // Swap
  getSwapsByBuyer,
  getAllSwaps,
  // Stats
  getGlobalStats,
};
